class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.mobileSearchTrigger = document.querySelector('.js--trigger-mobile-search');
    this.mobileSearchClose = document.querySelector('.js--mobile-close-search');
    this.mobileForm = document.querySelector('.mobile-search__form');
    this.scrollLocked = false;
    this.savedScrollY = 0;
    this.touchMoveHandler = null;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    // Close search when user scrolls more than 200px (throttled)
    this.scrollThrottled = false;
    this.initialScrollPosition = 0;
    this.scrollThreshold = 200; // pixels
    this.scrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    // Mobile search event listeners
    if (this.mobileSearchTrigger) {
      this.mobileSearchTrigger.addEventListener('click', this.openMobileSearch.bind(this));
    }
    if (this.mobileSearchClose) {
      this.mobileSearchClose.addEventListener('click', this.closeMobileSearch.bind(this));
    }

    // Close mobile search on Escape key
    document.addEventListener('keydown', this.onEscapeKey.bind(this));
  }

  onEscapeKey(event) {
    if (event.key === 'Escape') {
      if (this.mobileForm && this.mobileForm.style.display === 'block') {
        this.closeMobileSearch();
      }
    }
  }

  openMobileSearch() {
    if (window.innerWidth <= 989 && this.mobileForm) {
      const mobileInputId = this.input.id + '-mobile';
      const mobileInput = document.querySelector(`#${mobileInputId}`);
      
      this.mobileForm.style.display = 'block';
      if (mobileInput) {
        mobileInput.focus();
      }
      
      // Robust body scroll lock (including iOS)
      this.lockBodyScroll();
      
      // Update trigger/close button visibility
      if (this.mobileSearchTrigger) {
        this.mobileSearchTrigger.style.display = 'none';
      }
      if (this.mobileSearchClose) {
        this.mobileSearchClose.style.display = 'flex';
      }
    }
  }

  closeMobileSearch() {
    if (this.mobileForm) {
      this.mobileForm.style.display = 'none';
    }
    
    // Restore body scroll
    this.unlockBodyScroll();
    
    // Update trigger/close button visibility
    if (this.mobileSearchTrigger) {
      this.mobileSearchTrigger.style.display = 'flex';
    }
    if (this.mobileSearchClose) {
      this.mobileSearchClose.style.display = 'none';
    }
  }

  lockBodyScroll() {
    if (this.scrollLocked) return;
    this.savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    // Fix the body in place
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    // Prevent iOS scroll bounce outside allowed containers
    this.touchMoveHandler = (event) => {
      const target = event.target;
      const results = this.mobileForm && this.mobileForm.querySelector('.predictive-search--mobile');
      // Allow scrolling inside the results container if it exists
      if (results && results.contains(target)) return;
      event.preventDefault();
    };
    document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    this.scrollLocked = true;
  }

  unlockBodyScroll() {
    if (!this.scrollLocked) return;
    document.removeEventListener('touchmove', this.touchMoveHandler);
    this.touchMoveHandler = null;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this.savedScrollY || 0);
    this.scrollLocked = false;
  }

  onScroll() {
    if (this.scrollThrottled || !this.isOpen) return;
    
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDistance = Math.abs(currentScrollPosition - this.initialScrollPosition);
    
    if (scrollDistance >= this.scrollThreshold) {
      this.close(true);
      // Throttle to prevent excessive calls
      this.scrollThrottled = true;
      setTimeout(() => {
        this.scrollThrottled = false;
      }, 100);
    }
  }

  disconnectedCallback() {
    // Clean up scroll listener when component is removed
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    // Clean up escape key listener
    document.removeEventListener('keydown', this.onEscapeKey.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    // Find breadcrumb and fix z-index if it exists
    let breadCrumb = document.querySelector('.breadcrumbs');
    if (breadCrumb) {
      breadCrumb.style.zIndex = 1;
    }
    
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    // Handle mobile search UI updates
    if (window.innerWidth <= 989) {
      this.openMobileSearch();
    } else {
      // Desktop behavior
      if (this.mobileSearchTrigger) {
        this.mobileSearchTrigger.style.display = 'none';
      }
      if (this.mobileSearchClose) {
        this.mobileSearchClose.style.display = 'flex';
      }
    }
    
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        // Close mobile search if open
        if (window.innerWidth <= 989) {
          this.closeMobileSearch();
        }
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  // turnOffKeyboardPhone() {
  //   console.log('turnOffKeyboardPhone');
    
  // }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        // this.turnOffKeyboardPhone();

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Check if the markup contains actual results by looking for the results wrapper
    const hasResultsWrapper = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper');
    
    // If no results wrapper found, it means no results - show message
    if (!hasResultsWrapper) {
      const noResultsMessage = `
        <div class="predictive-search__no-results">
          <div class="predictive-search__no-results-content">
            <h3 class="predictive-search__no-results-title">No results</h3>
            <p class="predictive-search__no-results-text">We couldn't find anything for "<strong>${this.searchTerm}</strong>"</p>
            <p class="predictive-search__no-results-suggestion">Try different keywords or check your spelling.</p>
          </div>
        </div>
      `;
      this.predictiveSearchResults.innerHTML += noResultsMessage;
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    let breadCrumb = document.querySelector('.breadcrumbs');
    if (breadCrumb) {
      breadCrumb.style.zIndex = 1;
    }
    // Store initial scroll position for threshold detection
    this.initialScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    let breadCrumb = document.querySelector('.breadcrumbs');
    if (breadCrumb) {
      breadCrumb.style.zIndex = 5;
    }
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    
    // Close mobile search when closing predictive search
    if (window.innerWidth <= 989) {
      this.closeMobileSearch();
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
